import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Linkedin, Twitter, Mail } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { useTheme } from '../context/ThemeContext';

const footerLinks = {
  quickLinks: [
    { name: "Home", href: "/" },
    { name: "About", href: "/about" },
    { name: "Services", href: "/services" },
    { name: "Careers", href: "/careers" },
    { name: "Contact", href: "/contact" }
  ],
  legal: [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms & Conditions", href: "/terms" },
    { name: "Legal Disclaimers", href: "/legal" }
  ]
};

const Footer = () => {
  const { theme } = useTheme();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className={`relative border-t transition-colors duration-300 ${
      theme === 'dark' ? 'bg-black border-white/10' : 'bg-gray-50 border-gray-200'
    }`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[7.6923%] py-12 sm:py-14 lg:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-10">
          {/* Brand Column */}
          <div className="col-span-2">
            <Link to="/" className="inline-block mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_67218e68-4bb7-4a39-b578-c9fa413502c7/artifacts/omsxniwo_Connecting%20Top%20IT%20Talents%20to%20Success.png" 
                alt="RKS Technologies" 
                className="h-[150px] sm:h-[180px] lg:h-[210px] w-auto object-contain"
              />
            </Link>
            <p className={`text-sm sm:text-base leading-relaxed mb-5 max-w-[350px] ${
              theme === 'dark' ? 'text-white/60' : 'text-gray-600'
            }`}>
              {companyInfo.slogan}
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-3">
              {[Linkedin, Twitter, Mail].map((Icon, index) => (
                <a key={index}
                  href={index === 2 ? `mailto:${companyInfo.email}` : '#'}
                  className={`p-2.5 transition-all duration-300 ${
                    theme === 'dark' 
                      ? 'bg-white/5 border border-white/10 hover:border-[#00FFD1]/40 hover:bg-[#00FFD1]/10' 
                      : 'bg-white border border-gray-200 hover:border-[#00997A]/40 hover:bg-[#00997A]/10'
                  }`}
                  aria-label={['LinkedIn', 'Twitter', 'Email'][index]}
                >
                  <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${
                    theme === 'dark' ? 'text-white/60 hover:text-[#00FFD1]' : 'text-gray-500 hover:text-[#00997A]'
                  }`} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className={`font-semibold text-sm sm:text-base mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>Quick Links</h4>
            <ul className="space-y-2 sm:space-y-3">
              {footerLinks.quickLinks.map((link) => (
                <li key={link.name}>
                  <Link to={link.href}
                    className={`text-sm transition-colors ${
                      theme === 'dark' ? 'text-white/50 hover:text-white' : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className={`font-semibold text-sm sm:text-base mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>Contact</h4>
            <ul className="space-y-2 sm:space-y-3">
              <li>
                <a href={`mailto:${companyInfo.email}`}
                  className={`text-sm transition-colors ${
                    theme === 'dark' ? 'text-white/50 hover:text-[#00FFD1]' : 'text-gray-500 hover:text-[#00997A]'
                  }`}
                >
                  {companyInfo.email}
                </a>
              </li>
              <li>
                <a href={`tel:${companyInfo.phone}`}
                  className={`text-sm transition-colors ${
                    theme === 'dark' ? 'text-white/50 hover:text-[#00FFD1]' : 'text-gray-500 hover:text-[#00997A]'
                  }`}
                >
                  {companyInfo.phone}
                </a>
              </li>
              <li className={`text-sm ${theme === 'dark' ? 'text-white/50' : 'text-gray-500'}`}>
                {companyInfo.location}
              </li>
              <li className={`text-sm ${theme === 'dark' ? 'text-white/50' : 'text-gray-500'}`}>
                {companyInfo.hours}
              </li>
            </ul>

            <div className={`mt-5 pt-4 border-t ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
              <h5 className={`text-xs mb-2 underline ${theme === 'dark' ? 'text-white/40' : 'text-gray-400'}`}>Legal</h5>
              <ul className="space-y-1">
                {footerLinks.legal.map((link) => (
                  <li key={link.name}>
                    <Link to={link.href}
                      className={`text-xs transition-colors ${
                        theme === 'dark' ? 'text-white/30 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'
                      }`}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`mt-10 sm:mt-12 pt-6 sm:pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-4 ${
          theme === 'dark' ? 'border-white/10' : 'border-gray-200'
        }`}>
          <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-white/40' : 'text-gray-400'}`}>
            © {companyInfo.year} RKS Technologies. All Rights Reserved.
          </p>
          
          <button onClick={scrollToTop}
            className={`flex items-center gap-2 px-3 py-1.5 transition-all duration-300 ${
              theme === 'dark' 
                ? 'bg-white/5 border border-white/10 hover:border-[#00FFD1]/40 hover:bg-[#00FFD1]/10' 
                : 'bg-white border border-gray-200 hover:border-[#00997A]/40'
            }`}
          >
            <span className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-white/50' : 'text-gray-500'}`}>
              Back to top
            </span>
            <ArrowUp className={`w-3 h-3 sm:w-4 sm:h-4 ${
              theme === 'dark' ? 'text-white/50' : 'text-gray-500'
            }`} />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
